"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useTranslations } from "next-intl";

import Badge2026 from "@/components/sections/2026/ui/Badge2026";
import ReviewsCarousel from "./parts/ReviewsCarousel";
import { useReveal } from "./parts/reveal";
import { googleReviews, reviewsSummary } from "./data";
import ResponsiveCopy from "@/components/sections/2026/ui/ResponsiveCopy";

// "What our clients say" — a Google Reviews social-proof band between the About
// Us starfield and the News carousel. Header + a summary bar (Google mark,
// average rating, star row, review count, outbound link) sit above a draggable
// carousel of individual review cards. Static content (see data.js); dark theme
// to match its bg-black neighbours.

export default function GoogleReviewsSection() {
  const t = useTranslations("home.googleReviews");
  const { reveal } = useReveal({ amount: 0.2 });
  const { rating, count, url } = reviewsSummary;
  const roundedRating = Math.round(rating);

  if (!googleReviews || googleReviews.length === 0) return null;

  return (
    <section id="reviews" className="bg-black">
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-20">
        {/* Header */}
        <motion.div {...reveal} custom={0}>
          <Badge2026>{t("badge")}</Badge2026>
        </motion.div>

        <motion.h2
          {...reveal}
          custom={1}
          className="mt-6 text-3xl font-bold text-white sm:text-5xl lg:text-6xl"
        >
          <ResponsiveCopy
            desktop={t("headline")}
            mobile={t("headlineMobile")}
          />
        </motion.h2>

        <motion.p
          {...reveal}
          custom={2}
          className="mt-6 max-w-7xl text-base font-normal text-white/70 sm:text-lg"
        >
          <ResponsiveCopy
            desktop={t("subtitle")}
            mobile={t("subtitleMobile")}
          />
        </motion.p>

        {/* Carousel */}
        <motion.div {...reveal} custom={4} className="mt-12">
          <ReviewsCarousel reviews={googleReviews} />
        </motion.div>
      </div>
    </section>
  );
}
